import { Anchor, Paragraph } from '@zillow/constellation-10';
import { Fragment, useMemo } from 'react';
import { Rh_Disclaimer } from '../../types/GraphqlTypes';

type DisclaimerProps = {
  disclaimerText: Rh_Disclaimer['disclaimerText'];
  links?: Rh_Disclaimer['links'];
  emailLinks?: Rh_Disclaimer['emailLinks'];
};

/**
 * A component that renders a dynamic disclaimer with links.
 *
 * The disclaimer text is passed as a string, and any placeholders within the text are
 * replaced with an <a> tag with the link URL and label.
 *
 * @example
 * ```tsx
 * {
 *   "disclaimerText": "This is a disclaimer. View our [privacy-policy] and [terms-of-use] for more information.",
 *   "links": [
 *     {
 *       "textToReplace": "[privacy-policy]",
 *       "textToDisplay": "Privacy Policy",
 *       "url": "/privacy-policy"
 *     },
 *     {
 *       "textToReplace": "[terms-of-use]",
 *       "textToDisplay": "Terms of Use",
 *       "url": "/terms-of-use"
 *     }
 *   ],
 *    "emailLinks": [
 *     {
 *       "textToReplace": "[z-rental-partners-email]",
 *       "textToDisplay": "rentalpartners@zillowgroup.com",
 *       "emailAddress": "rentalpartners@zillowgroup.com"
 *     },
 * }
 * ```
 *
 * The component splits the disclaimer text into segments at each link
 * placeholder (e.g. [privacy-policy]), and maps over the resulting array of
 * segments. If the current segment is a link placeholder, it renders an <a>
 * tag with the link URL and label. If it's not a link, it renders the segment as
 * plain text.
 */
export default function Disclaimer({ disclaimerText, links, emailLinks }: DisclaimerProps) {
  const allLinks = useMemo(() => {
    const combinedLinks = [...(links || []), ...(emailLinks || [])];
    return new Map(combinedLinks.map((link) => [link?.textToReplace, link]));
  }, [links, emailLinks]);

  // Early return if no links - just render text as-is
  if (!allLinks.size) {
    return (
      <Paragraph textStyle="body-xs" css={{ marginTop: 'tighter' }}>
        {disclaimerText}
      </Paragraph>
    );
  }

  return (
    <Paragraph textStyle="body-xs" css={{ marginTop: 'tighter' }}>
      {disclaimerText.split(/(\[.*?\])/).map((segment, index) => {
        const link = allLinks.get(segment);

        if (!link) {
          return <Fragment key={index}>{segment}</Fragment>;
        }

        const { textToDisplay } = link;
        const isEmail = 'emailAddress' in link;
        const href = isEmail ? `mailto:${link.emailAddress}` : link.url;

        return (
          <Anchor key={segment} href={href} target={isEmail ? undefined : '_blank'}>
            {textToDisplay}
          </Anchor>
        );
      })}
    </Paragraph>
  );
}
